import { getCookie } from 'public/assets/js/analytics/cookie';
import type { FC } from 'react';

const STATIC_COOKIE_NAME = 'LD_COOKIE';

/**
 * Get a cookie value as it was at page load. Does not reflect
 * changes to the cookie value after page load.
 */
export function getStaticCookie(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!(STATIC_COOKIE_NAME in window)) {
    // eslint-disable-next-line no-console
    console.error(
      `${STATIC_COOKIE_NAME} not initialized early enough. Make sure it's placed after <head>. Falling back to \`document.cookie\`.`,
    );
  }

  return (
    decodeURIComponent(
      getCookie(name, window[STATIC_COOKIE_NAME] ?? document.cookie),
    ) || null // prefer `null` over `""`
  );
}

/**
 * LaunchDarkly static cookie
 *
 * This should be placed right after `<head>`.
 *
 * Cookies are used to communicate LaunchDarkly experimentation data (specifically, `ld_flags`) from the backend
 * to the frontend for the current page. This value can change page to page, meaning the point at which we
 * read the cookie could return different values if multiple different A/B experiment page requests are
 * being made simultaneously. To reduce the likelihood of this, we read the cookie immediately at
 * page load, increasing the likelihood that the cookie value we read is the one the
 * server sent along with the current page document HTTP response.
 */
const BootLaunchDarkly: FC = () => {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.${STATIC_COOKIE_NAME}=document.cookie`,
      }}
    />
  );
};

export default BootLaunchDarkly;
